import React, { useState } from 'react';
import { Shield, Building2, Phone, Mail, FileCheck } from 'lucide-react';

interface RegistrationFormData {
  companyName: string;
  tradingName: string;
  registrationNumber: string;
  fcaNumber: string;
  vatNumber: string;
  registeredAddress: string;
  operatingAddress: string;
  countryOfOperation: string;
  websiteUrl: string;
  primaryContactName: string;
  jobTitle: string;
  primaryEmail: string;
  phoneNumber: string;
  claimsEmail: string;
  supportEmail: string;
  emergencyPhone: string;
  insuranceProducts: string[];
  underwriterInfo: string;
  termsAccepted: boolean;
}

export function InsurerRegistrationForm() {
  const [formData, setFormData] = useState<RegistrationFormData>({
    companyName: '',
    tradingName: '',
    registrationNumber: '',
    fcaNumber: '',
    vatNumber: '',
    registeredAddress: '',
    operatingAddress: '',
    countryOfOperation: 'United Kingdom',
    websiteUrl: '',
    primaryContactName: '',
    jobTitle: '',
    primaryEmail: '',
    phoneNumber: '',
    claimsEmail: '',
    supportEmail: '',
    emergencyPhone: '',
    insuranceProducts: [],
    underwriterInfo: '',
    termsAccepted: false
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleInputChange = (field: keyof RegistrationFormData, value: string | boolean | string[]) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleProductChange = (product: string) => {
    setFormData(prev => ({
      ...prev,
      insuranceProducts: prev.insuranceProducts.includes(product)
        ? prev.insuranceProducts.filter(p => p !== product)
        : [...prev.insuranceProducts, product]
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      // Form validation
      if (!formData.termsAccepted) {
        throw new Error('You must accept the terms and conditions');
      }

      // TODO: Submit form data to backend
      console.log('Form submitted:', formData);
      
      // Show success message
      alert('Registration submitted successfully! We will review your application and contact you soon.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit registration');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6 bg-white rounded-lg shadow-lg">
      <div className="flex items-center space-x-3 mb-8">
        <Shield className="w-8 h-8 text-indigo-600" />
        <h1 className="text-2xl font-bold text-gray-900">Insurance Provider Registration</h1>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md text-red-600">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Company Information */}
        <section>
          <h2 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
            <Building2 className="w-5 h-5 mr-2 text-indigo-600" />
            Company Information
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Company Name (Registered Name)
              </label>
              <input
                type="text"
                value={formData.companyName}
                onChange={(e) => handleInputChange('companyName', e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Trading Name (If different)
              </label>
              <input
                type="text"
                value={formData.tradingName}
                onChange={(e) => handleInputChange('tradingName', e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Company Registration Number
              </label>
              <input
                type="text"
                value={formData.registrationNumber}
                onChange={(e) => handleInputChange('registrationNumber', e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                FCA/Regulatory Number
              </label>
              <input
                type="text"
                value={formData.fcaNumber}
                onChange={(e) => handleInputChange('fcaNumber', e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                VAT Number (If applicable)
              </label>
              <input
                type="text"
                value={formData.vatNumber}
                onChange={(e) => handleInputChange('vatNumber', e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Website URL
              </label>
              <input
                type="url"
                value={formData.websiteUrl}
                onChange={(e) => handleInputChange('websiteUrl', e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                required
              />
            </div>
          </div>
          <div className="mt-4 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Registered Address
              </label>
              <textarea
                value={formData.registeredAddress}
                onChange={(e) => handleInputChange('registeredAddress', e.target.value)}
                rows={3}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Operating Address (If different)
              </label>
              <textarea
                value={formData.operatingAddress}
                onChange={(e) => handleInputChange('operatingAddress', e.target.value)}
                rows={3}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
              />
            </div>
          </div>
        </section>

        {/* Contact Information */}
        <section>
          <h2 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
            <Phone className="w-5 h-5 mr-2 text-indigo-600" />
            Contact Information
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Primary Contact Name
              </label>
              <input
                type="text"
                value={formData.primaryContactName}
                onChange={(e) => handleInputChange('primaryContactName', e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Job Title
              </label>
              <input
                type="text"
                value={formData.jobTitle}
                onChange={(e) => handleInputChange('jobTitle', e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Primary Email
              </label>
              <input
                type="email"
                value={formData.primaryEmail}
                onChange={(e) => handleInputChange('primaryEmail', e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Phone Number
              </label>
              <input
                type="tel"
                value={formData.phoneNumber}
                onChange={(e) => handleInputChange('phoneNumber', e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Claims Email
              </label>
              <input
                type="email"
                value={formData.claimsEmail}
                onChange={(e) => handleInputChange('claimsEmail', e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Customer Support Email
              </label>
              <input
                type="email"
                value={formData.supportEmail}
                onChange={(e) => handleInputChange('supportEmail', e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Emergency / Claims Phone Number
              </label>
              <input
                type="tel"
                value={formData.emergencyPhone}
                onChange={(e) => handleInputChange('emergencyPhone', e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                required
              />
            </div>
          </div>
        </section>

        {/* Insurance Products */}
        <section>
          <h2 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
            <Shield className="w-5 h-5 mr-2 text-indigo-600" />
            Insurance Products & Services
          </h2>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              {[
                'Car Insurance',
                'Home Insurance',
                'Life Insurance',
                'Health Insurance',
                'Business Insurance',
                'Liability Insurance',
                'Travel Insurance'
              ].map((product) => (
                <label key={product} className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    checked={formData.insuranceProducts.includes(product)}
                    onChange={() => handleProductChange(product)}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                  />
                  <span className="text-sm text-gray-700">{product}</span>
                </label>
              ))}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Underwriter Information
              </label>
              <textarea
                value={formData.underwriterInfo}
                onChange={(e) => handleInputChange('underwriterInfo', e.target.value)}
                rows={4}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                placeholder="Please provide details about your underwriting arrangements..."
                required
              />
            </div>
          </div>
        </section>

        {/* Terms & Compliance */}
        <section>
          <h2 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
            <FileCheck className="w-5 h-5 mr-2 text-indigo-600" />
            Terms & Compliance
          </h2>
          <div className="space-y-4">
            <div className="bg-gray-50 p-4 rounded-md">
              <p className="text-sm text-gray-600 mb-4">
                By submitting this form, you confirm that:
              </p>
              <ul className="list-disc list-inside text-sm text-gray-600 space-y-2">
                <li>All information provided is accurate and complete</li>
                <li>Your company holds all necessary insurance licenses and regulatory approvals</li>
                <li>You have appropriate data protection measures in place</li>
                <li>You agree to our terms of service and data processing agreement</li>
              </ul>
            </div>
            <label className="flex items-center space-x-3">
              <input
                type="checkbox"
                checked={formData.termsAccepted}
                onChange={(e) => handleInputChange('termsAccepted', e.target.checked)}
                className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                required
              />
              <span className="text-sm text-gray-700">
                I agree to the Terms & Conditions and Privacy Policy
              </span>
            </label>
          </div>
        </section>

        <div className="flex justify-end space-x-4">
          <button
            type="button"
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            Save Draft
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className={`px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 ${
              isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            {isSubmitting ? 'Submitting...' : 'Submit Registration'}
          </button>
        </div>
      </form>
    </div>
  );
}